/**
 * Whether this console is hosted inside the Nimbalyst phone app.
 *
 * The app injects a document-start script that sets
 * `window.__NIMBALYST_EMBED__ = { platform, appVersion, bridge }` before any
 * console code runs. It is read once, here, and nowhere else: no user-agent
 * sniffing and no query parameter that a reload or an in-app link could drop.
 * A malformed marker is treated as absent, so a stray global cannot switch a
 * desktop browser into a mode that refuses to show the login page.
 */

export type EmbedPlatform = 'ios' | 'android';

export interface EmbedMarker {
  platform: EmbedPlatform;
  appVersion: string;
  /** The native message handler name (`webkit.messageHandlers[bridge]` / `window[bridge]`). */
  bridge: string;
}

declare global {
  interface Window {
    __NIMBALYST_EMBED__?: unknown;
  }
}

export function parseEmbedMarker(value: unknown): EmbedMarker | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  if (record.platform !== 'ios' && record.platform !== 'android') return null;
  if (typeof record.appVersion !== 'string' || record.appVersion.length > 64) return null;
  if (typeof record.bridge !== 'string' || !/^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(record.bridge)) return null;
  return { platform: record.platform, appVersion: record.appVersion, bridge: record.bridge };
}

let cached: { marker: EmbedMarker | null } | null = null;

/** The marker as injected at document start, read once per page load. */
export function getEmbedMarker(): EmbedMarker | null {
  if (!cached) {
    cached = { marker: typeof window === 'undefined' ? null : parseEmbedMarker(window.__NIMBALYST_EMBED__) };
  }
  return cached.marker;
}

export function isEmbedded(): boolean {
  return getEmbedMarker() !== null;
}

/** Tests only: forget the cached read so the next call sees the current global. */
export function resetEmbedMarkerForTests(): void {
  cached = null;
}

/**
 * Set to "true" on an ancestor to make every tracker view embed below it
 * read-only: no cell edits, no create row. Mirrors
 * `TRACKER_EMBEDS_READ_ONLY_ATTRIBUTE` in collab-client's `TrackerViewEmbed`.
 */
export const TRACKER_EMBEDS_READ_ONLY_ATTRIBUTE = 'data-tracker-embeds-read-only';

/** Spread onto a wrapper: marks tracker embeds read-only inside the phone app, nothing elsewhere. */
export function embeddedReadOnlyTrackerEmbeds(): Record<string, string> {
  return isEmbedded() ? { [TRACKER_EMBEDS_READ_ONLY_ATTRIBUTE]: 'true' } : {};
}
